import { CheckIcon } from "@radix-ui/react-icons"
import { redirect, type ActionFunctionArgs } from "@remix-run/node"
import { Link, useSearchParams } from "@remix-run/react"
import { withZod } from "@remix-validated-form/with-zod"
import axios from "axios"
import { ValidatedForm, validationError } from "remix-validated-form"
import { z } from "zod"
import { FormInput, FormText, SubmitButton } from "~/components/form-fields"
import Header from "~/components/header"
import Typography from "~/components/typography"
import { Alert, AlertDescription } from "~/components/ui/alert"
import { Separator } from "~/components/ui/separator"
import { Public } from "~/utils/remix"

const removalSchema = z
  .object({
    Email: z.string().email({ message: "Invalid email format" }).min(1, { message: "email is required" }),
    LegalName: z.string().min(1, { message: "legal name is required" }),
    Urls: z.string().min(1, { message: "urls are required" }),
    Info: z.string().optional(),
    Signature: z.string().min(1, { message: "signature is required" }),
  })
  .refine((data) => data.Signature === data.LegalName, {
    message: "Signature must match legal name",
    path: ["Signature"],
  })
export const removalValidator = withZod(removalSchema)

export const action = Public(async ({ request, context }: ActionFunctionArgs) => {
  const result = await removalValidator.validate(await request.formData())
  if (result.error) {
    return validationError(result.error)
  }

  const response = await axios.post(
    "https://hooks.slack.com/triggers/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",
    {
      email: result.data?.Email,
      urls: result.data?.Urls,
      name: result.data?.LegalName,
      info: result.data?.Info,
      title: `New content removal request from ${result.data.LegalName} - ${result.data.Email}`,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  )
  if (response.status !== 200) {
    return new Response("Error sending email", { status: 500 })
  }

  return redirect("/content-removal?success=true")
})

export default function ContentRemoval() {
  const [data] = useSearchParams()

  return (
    <div className="max-w-5xl m-auto mt-16">
      <Header title="Content Removal" hideBanner={true} />
      {data?.get("success") === "true" && (
        <Alert variant="success" className="my-4 pt-4">
          <CheckIcon className="w-5 h-5" />
          <AlertDescription className="text-base">
            We've received your request and will respond shortly.
          </AlertDescription>
        </Alert>
      )}
      <div className="mx-4 md:mx-16">
        <Typography className="max-w-none">
          <p>We designed MakeLoveNotPorn to be the safest place on the internet.</p>

          <p>
            <b>MakeLoveNotPornstars:</b> the moment you want your videos removed, we remove them. Just email{" "}
            <Link to="mailto:curators@makelovenotporn.tv">curators@makelovenotporn.tv</Link> - allow a maximum of 48
            hours for us to fulfill your request, depending on when we receive your email . Most often, we are able to
            remove your videos immediately, by which we mean within an hour.
          </p>

          <p>
            <b>MakeLoveNotPorn members:</b> at MakeLoveNotPorn we operate a unique human-curation model.
          </p>

          <p>There is no self-publishing of anything.</p>

          <p>
            Our curation team reviews all legal documentation submitted to prove that all MakeLoveNotPornstars are 18 or
            over. Our curation team watches every video submitted from beginning to end, to decide whether or not we
            will publish it - all videos must be legal, consensual and real. Our curation team contacts each participant
            individually to confirm that they consent to the video being published. Only then, do we publish the video.
          </p>

          <p>
            That way, we ensure that we never publish anything on MakeLoveNotPorn that violates our{" "}
            <Link to="/terms-of-service-agreement">terms of service</Link>, because we are able to review and reject
            anything that does that, before publishing.
          </p>

          <p>
            Our curation team has a personal relationship with every MakeLoveNotPornstar and we know who each one is.
          </p>

          <p>
            (We also review every post on every member profile, and every comment on every videos, before we approve, or
            reject, and publish those posts and those comments.)
          </p>

          <Separator className="my-2" orientation="horizontal" />

          <p>
            <b>
              If you are concerned about anything you see in any of our fully human-curated videos, we invite you to
              contact us immediately. Please fill out the following form:
            </b>
          </p>
        </Typography>

        <ValidatedForm
          className="mx-auto w-full max-w-2xl mt-6"
          action="/content-removal"
          validator={removalValidator}
          method="post">
          <div className="flex flex-col gap-4">
            <FormInput type="input" name="Email" label="Email" />
            <FormInput type="input" name="LegalName" label="Legal Name" />
            <FormText
              name="Urls"
              label="URLs of the content you are reporting that concerns you:"
              inputClasses="min-h-32 h-32 bg-background text-xs px-3"
            />
            <FormText
              name="Info"
              label="Any additional information:"
              inputClasses="min-h-32 h-32 bg-background text-xs px-3"
            />

            <Separator className="my-2" orientation="horizontal" />

            <Typography>
              <h3>Digital Signature</h3>
              <p>
                By typing your name in the field below, you guarantee that you are the person being named and
                represented on this form and that all provided information is accurate.
              </p>
            </Typography>

            <FormInput type="input" name="Signature" />
            <SubmitButton className="w-48" content="Submit" loading="Submitting..." />
          </div>
        </ValidatedForm>
      </div>
    </div>
  )
}
